import { useState, useRef } from 'react';
import { ExerciseBlock, ExerciseTarget } from '@/types/workout';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { workoutSessionApi } from '@/services/workoutSessionApi';

interface ExerciseSettingsProps {
  exercise: ExerciseBlock;
  onUpdate: (updates: Partial<ExerciseBlock>) => void;
  workoutId?: string;
}

export function ExerciseSettings({ exercise, onUpdate, workoutId }: ExerciseSettingsProps) {
  const { target } = exercise;
  const [draftSessionId, setDraftSessionId] = useState<string | null>(null);
  const hasCreatedDraftRef = useRef(false);

  const updateTarget = async (updates: Partial<ExerciseTarget>) => {
    // Update local state immediately
    onUpdate({
      target: { ...target, ...updates },
    });

    // Create/get draft session on first edit if workoutId is provided
    if (workoutId && !hasCreatedDraftRef.current) {
      try {
        const sessionResponse = await workoutSessionApi.getOrCreateDraftSession(workoutId);
        setDraftSessionId(sessionResponse.session_id);
        hasCreatedDraftRef.current = true;

        // Update metadata for all sets of this exercise
        await updateSetMetadataInSession(sessionResponse.session_id, updates);
      } catch (error) {
        console.error('Failed to create/update draft session:', error);
      }
    } else if (draftSessionId) {
      // Update existing draft session
      await updateSetMetadataInSession(draftSessionId, updates);
    }
  };

  // Handle Work control changes (unified mode + value)
  const handleWorkChange = async (updates: { mode: 'reps' | 'time'; repsValue: number | null; timeSeconds: number | null }) => {
    const targetUpdates: Partial<ExerciseTarget> = {
      mode: updates.mode,
      reps: updates.repsValue ?? undefined,
      duration_seconds: updates.timeSeconds ?? undefined,
    };
    await updateTarget(targetUpdates);
  };

  const updateSetMetadataInSession = async (
    sessionId: string,
    metadata: Partial<ExerciseTarget>
  ) => {
    if (!workoutId) return;

    try {
      // Generate set IDs based on exercise ID and number of sets
      const numSets = target.mode === 'time' ? (target.sets || 1) : (target.sets || 1);
      
      // Update metadata for all sets of this exercise
      const updatePromises = [];
      for (let i = 1; i <= numSets; i++) {
        const setId = `${exercise.id}-set-${i}`;
        const setMetadata: any = {};
        
        if (metadata.weight !== undefined) setMetadata.weight = metadata.weight;
        if (metadata.reps !== undefined) setMetadata.reps = metadata.reps;
        if (metadata.duration_seconds !== undefined) setMetadata.duration_seconds = metadata.duration_seconds;
        if (metadata.rpe !== undefined) setMetadata.rpe = metadata.rpe;

        // Only update if there's actual metadata to save
        if (Object.keys(setMetadata).length > 0) {
          updatePromises.push(
            workoutSessionApi.updateSetMetadata(sessionId, setId, setMetadata).catch((err) => {
              // If set doesn't exist yet (404), that's okay - it will be created when workout starts
              if (err.response?.status !== 404) {
                console.error(`Failed to update metadata for set ${setId}:`, err);
              }
            })
          );
        }
      }
      
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Failed to update set metadata in session:', error);
    }
  };

  return (
    <div className="space-y-3">
      {/* Advanced fields - only show for reps mode */}
      {target.mode === 'reps' && (
        <>
          <div>
            <Label htmlFor={`weight-${exercise.id}`}>Weight (optional)</Label>
            <Input
              id={`weight-${exercise.id}`}
              type="number"
              value={target.weight || ''}
              onChange={(e) => updateTarget({ weight: parseFloat(e.target.value) || undefined })}
              className="mt-1"
              placeholder="kg"
            />
          </div>
          <div>
            <Label htmlFor={`rpe-${exercise.id}`}>RPE (optional, 1-10)</Label>
            <Input
              id={`rpe-${exercise.id}`}
              type="number"
              min="1"
              max="10"
              value={target.rpe || ''}
              onChange={(e) => updateTarget({ rpe: parseInt(e.target.value) || undefined })}
              className="mt-1"
            />
          </div>
        </>
      )}
    </div>
  );
}

